<script>
import DataTable from 'datatables.net-vue3';
import moment from 'moment';
import { getImportBatches } from '@/helpers/api/mdm/mdm-command-logs.api';
import { commonService } from '@/helpers/common.service';

export default {
    name: 'MdmBatchApprovalList',

    components: {
        DataTable,
    },

    props: {
        processType: { type: Number, required: true },
        isApprover:  { type: Boolean, default: false },
    },

    data() {
        return {
            batches: [],
            loading: true,
            dtKey:   0,
            filterStatus: null,
            options: {
                searching:  true,
                responsive: true,
                columnDefs: [
                    { orderable: false, targets: [0, 1, 2, 3, 5] },
                ],
                order: [[4, 'desc']], // Date Info (Chronological)
                language: {
                    emptyTable: "No batches found."
                }
            },
        };
    },

    methods: {
        async fetchBatches() {
            this.loading = true;
            try {
                // Fetch all batches for this process type
                const params = {
                    type:     this.processType,
                    status:   this.filterStatus,
                    paginate: 0,
                };
                const response = await getImportBatches(params);
                this.batches = response.data?.data ?? response.data ?? [];
                this.dtKey += 1;
            } catch (error) {
                const msg = error.response?.data?.message ?? error.message;
                commonService.swalPrompt(false, msg, 'Failed to load batches');
            } finally {
                this.loading = false;
            }
        },

        viewRecords(batch) {
            this.$emit('view-records', batch);
        },

        format_date(value) {
            if (value) {
                return moment(String(value)).format('DD-MM-YYYY HH:mm:ss');
            }
            return '---';
        },

        statusClass(status) {
            const map = {
                0: 'badge-soft-secondary', // Uploaded
                1: 'badge-soft-info',      // Queue
                2: 'badge-soft-primary',   // Processing
                3: 'badge-soft-warning',   // Waiting Approval
                4: 'badge-soft-success',   // Approved
                5: 'badge-soft-dark',      // Rejected
                6: 'badge-soft-danger',    // Failed
            };
            return map[status] ?? 'badge-soft-secondary';
        },
    },

    watch: {
        processType: {
            handler() { this.fetchBatches(); },
            immediate: true
        },
        filterStatus: {
            handler() { this.fetchBatches(); },
        }
    },
};
</script>

<template>
    <div class="card shadow-none border-0 mb-0">
        <div class="card-header border-bottom-dashed py-3 px-0">
            <div class="d-flex align-items-center justify-content-between">
                <h5 class="card-title mb-0 fs-14 fw-bold">MDM Import Batches</h5>
                <div class="d-flex align-items-center gap-2">
                    <div class="d-flex align-items-center gap-2">
                        <label class="mb-0 fs-11 text-muted text-uppercase fw-bold">Status Filter:</label>
                        <select v-model="filterStatus" class="form-select form-select-sm fs-12 py-1 shadow-none border-light-subtle" style="width: 150px;">
                            <option :value="null">All Statuses</option>
                            <option :value="0">Uploaded</option>
                            <option :value="1">Queue</option>
                            <option :value="2">Processing</option>
                            <option :value="3">Waiting Approval</option>
                            <option :value="4">Approved</option>
                            <option :value="5">Rejected</option>
                            <option :value="6">Failed</option>
                        </select>
                    </div>
                    <button 
                        class="btn btn-soft-primary btn-sm" 
                        @click="fetchBatches" 
                        :disabled="loading"
                    >
                        <i class="ri-refresh-line align-bottom"></i>
                    </button>
                </div>
            </div>
        </div>

        <div class="card-body px-0 pt-0">
            <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-success" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>

            <div v-else class="table-responsive">
                <DataTable 
                    :key="dtKey" 
                    :options="options" 
                    class="table table-hover table-nowrap align-middle mb-0"
                >
                    <thead class="table-light">
                        <tr>
                            <th style="width: 20px;">No.</th>
                            <th>Batch Detail</th>
                            <th>Requester</th>
                            <th>Records</th>
                            <th>Date Info</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(batch, index) in batches" :key="batch.id">
                            <td class="text-center">{{ index + 1 }}</td>
                            
                            <!-- Batch ID & File -->
                            <td>
                                <div class="fw-medium text-primary mb-1">Batch #{{ batch.id }}</div>
                                <div class="text-muted fs-11 text-truncate" style="max-width: 200px;" :title="batch.filename">
                                    {{ batch.filename || '---' }}
                                </div>
                                <div class="mt-1">
                                    <span :class="['badge', statusClass(batch.status), 'fs-10']">
                                        {{ batch.status_label || 'Unknown' }}
                                    </span>
                                </div>
                            </td>
                            
                            <!-- Requester -->
                            <td>
                                <div v-if="batch.importer" class="d-flex align-items-center">
                                    <div class="flex-grow-1">
                                        <h6 class="fs-12 mb-0">{{ batch.importer.name }}</h6>
                                        <p v-if="batch.importer.email" class="text-muted fs-10 mb-0">{{ batch.importer.email }}</p>
                                    </div>
                                </div>
                                <span v-else class="text-muted fs-12">---</span>
                            </td>

                            <!-- Stats -->
                            <td>
                                <div class="text-dark fw-bold fs-12 mb-1">{{ batch.total_records ?? 0 }} Rows</div>
                                <div class="text-muted fs-10">MDM Command Logs</div>
                            </td>

                            <!-- Date Info -->
                            <td :data-order="batch.created_at">
                                <div class="text-muted fs-11 mb-1">
                                    <span class="fw-medium">Created:</span> {{ format_date(batch.created_at) }}
                                </div>
                                <div v-if="batch.completed_at" class="text-muted fs-11">
                                    <span class="fw-medium text-success">Processed:</span> {{ format_date(batch.completed_at) }}
                                </div>
                            </td>

                            <!-- Actions -->
                            <td>
                                    <button 
                                        class="btn btn-soft-info btn-sm px-3" 
                                        @click="viewRecords(batch)"
                                        data-bs-toggle="modal"
                                        data-bs-target="#mdmBatchRecordsModal"
                                        title="View Individual Records"
                                    >
                                        <i class="ri-eye-line align-bottom me-1"></i> View Records
                                    </button>
                            </td>
                        </tr>
                    </tbody>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<style scoped>
.table thead th { font-weight: 600; text-transform: uppercase; font-size: 11px; padding: 12px 10px; }
</style>
